/**
 * UranIA PA — Pseudonimizzazione `user_id_hash` per audit UI (US-028 AC#3).
 *
 * Il campo `user_id_hash` dell'evento `DisclosureBannerClicked` (analisi
 * §19 P.6/P.8) deve identificare la sessione utente senza esporre il
 * `sub` OIDC in chiaro nel transport client→audit-ingest: gli auditor
 * AgID hanno bisogno di correlare le interazioni con la stessa identità
 * pseudonima, non di re-identificare l'utente dal canale UI.
 *
 * Implementazione: SHA-256 hex del `subject` claim OIDC via
 * `crypto.subtle.digest`. Pseudonimizzazione one-way; il salt è
 * volutamente omesso perché il pairing `sub ↔ user_id_hash` è già
 * conoscibile lato server (Keycloak + audit-events Postgres) e un
 * salt per-deploy non rafforza il modello di minaccia rispetto al
 * canale browser→audit-ingest che è già `keepalive: true, credentials:
 * include` (vedi `audit-client.ts`).
 *
 * Fallback:
 *   - `subject == null` (utente anonimo, es. preview Vite senza JWT)
 *     ⇒ ritorna stringa vuota; il server riconosce il caso
 *     "anonymous" via lunghezza 0.
 *   - `crypto.subtle` indisponibile (ambiente test SSR / vecchi
 *     browser) ⇒ stringa vuota: meglio rinunciare al campo che
 *     emettere un PRNG fallace.
 */

export async function computeUserIdHash(subject: string | null): Promise<string> {
  if (subject === null || subject.length === 0) {
    return '';
  }
  if (typeof crypto === 'undefined' || typeof crypto.subtle === 'undefined') {
    return '';
  }
  const buffer = new TextEncoder().encode(subject);
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
